import React from "react";
import {
  Box,
  Container,
  Heading,
  VStack,
  HStack,
  Button,
  Card,
  CardBody,
  Text,
  Textarea,
  Alert,
  AlertIcon,
  Spinner,
  FormControl,
  FormLabel,
  Tabs,
  TabList,
  TabPanels,
  TabPanel,
  Tab,
} from "@chakra-ui/react";
import { ArrowBackIcon } from "@chakra-ui/icons";

import { InstitutionConfigProvider, useInstitutionConfig } from "../components/admin/config/InstitutionConfigContext";
import { getVisibleTabs } from "../components/admin/config/roleVisibility";

import GeneralSettingsTab from "../components/admin/config/GeneralSettingsTab";
import FeatureSettingsTab from "../components/admin/config/FeatureSettingsTab";
import FormsComplianceTab from "../components/admin/config/FormsComplianceTab";
import EvaluationsTab from "../components/admin/config/EvaluationsTab";
import ScenarioAccessTab from "../components/admin/config/ScenarioAccessTab";
import CohortManagementTab from "../components/admin/config/CohortManagementTab";
import SecurityTab from "../components/admin/config/SecurityTab";

const TAB_COMPONENTS = {
  'general': GeneralSettingsTab,
  'features': FeatureSettingsTab,
  'forms-compliance': FormsComplianceTab,
  'evaluations': EvaluationsTab,
  'scenario-access': ScenarioAccessTab,
  'cohorts': CohortManagementTab,
  'security': SecurityTab,
};

function InstitutionConfigContent() {
  const {
    config,
    fetchError,
    fetching,
    updateError,
    updating,
    formData,
    reason,
    setReason,
    session,
    decodedInstitution,
    navigate,
    handleSubmit,
    resetForm,
  } = useInstitutionConfig();

  const visibleTabs = getVisibleTabs(session);

  if (fetching) {
    return (
      <Box bg="gray.900" minH="100vh" color="white">
        <Container maxW="container.xl" py={8}>
          <VStack spacing={8} align="center" justify="center" minH="50vh">
            <Spinner size="xl" color="blue.400" />
            <Text>Loading institution configuration...</Text>
          </VStack>
        </Container>
      </Box>
    );
  }

  if (fetchError) {
    return (
      <Box bg="gray.900" minH="100vh" color="white">
        <Container maxW="container.xl" py={8}>
          <VStack spacing={6}>
            <HStack w="full" justify="space-between">
              <Button
                leftIcon={<ArrowBackIcon />}
                variant="ghost"
                color="blue.400"
                onClick={() => navigate("/admin/institutions")}
              >
                Back to Institutions
              </Button>
            </HStack>
            <Alert status="error">
              <AlertIcon />
              <VStack align="start" spacing={2}>
                <Text fontWeight="bold">Failed to load institution configuration</Text>
                <Text fontSize="sm">
                  {fetchError?.message || "Unknown error occurred"}
                </Text>
              </VStack>
            </Alert>
          </VStack>
        </Container>
      </Box>
    );
  }

  if (!config) {
    return (
      <Box bg="gray.900" minH="100vh" color="white">
        <Container maxW="container.xl" py={8}>
          <VStack spacing={6}>
            <HStack w="full" justify="space-between">
              <Button
                leftIcon={<ArrowBackIcon />}
                variant="ghost"
                color="blue.400"
                onClick={() => navigate("/admin/institutions")}
              >
                Back to Institutions
              </Button>
            </HStack>
            <Alert status="info">
              <AlertIcon />
              <VStack align="start" spacing={2}>
                <Text fontWeight="bold">No configuration found</Text>
                <Text fontSize="sm">
                  This institution doesn't have a configuration yet.
                </Text>
              </VStack>
            </Alert>
          </VStack>
        </Container>
      </Box>
    );
  }

  return (
    <Box bg="gray.900" minH="100vh" color="white">
      <Container maxW="container.xl" py={8}>
        <form onSubmit={handleSubmit}>
          <VStack spacing={8} align="stretch">
            {/* Header */}
            <HStack w="full" justify="space-between">
              <VStack align="start" spacing={2}>
                <Button
                  leftIcon={<ArrowBackIcon />}
                  variant="ghost"
                  color="blue.400"
                  onClick={() => navigate("/admin/institutions")}
                  size="sm"
                >
                  Back to Institutions
                </Button>
                <Heading size="lg" color="white">
                  {decodedInstitution} Configuration
                </Heading>
              </VStack>
              <HStack spacing={4}>
                <Button
                  type="button"
                  variant="outline"
                  colorScheme="gray"
                  onClick={resetForm}
                >
                  Reset
                </Button>
                <Button
                  type="submit"
                  colorScheme="blue"
                  isLoading={updating}
                  loadingText="Updating..."
                >
                  Update Configuration
                </Button>
              </HStack>
            </HStack>

            {/* Update Error */}
            {updateError && (
              <Alert status="error">
                <AlertIcon />
                <VStack align="start" spacing={1}>
                  <Text fontWeight="bold">Update failed</Text>
                  <Text fontSize="sm">{updateError.message}</Text>
                </VStack>
              </Alert>
            )}

            {/* Reason for Update */}
            <Card bg="gray.800" borderColor="gray.600">
              <CardBody>
                <FormControl>
                  <FormLabel color="white">Reason for Update (Optional)</FormLabel>
                  <Textarea
                    value={reason}
                    onChange={(e) => setReason(e.target.value)}
                    placeholder="(Optional) Describe the reason for this configuration change..."
                    bg="gray.700"
                    borderColor="gray.600"
                    color="white"
                    _placeholder={{ color: "gray.400" }}
                  />
                </FormControl>
              </CardBody>
            </Card>

            {/* Tabbed Content */}
            <Tabs isLazy colorScheme="blue" variant="enclosed">
              <TabList
                overflowX="auto"
                overflowY="hidden"
                css={{
                  '&::-webkit-scrollbar': { display: 'none' },
                  scrollbarWidth: 'none',
                }}
              >
                {visibleTabs.map(tab => (
                  <Tab
                    key={tab.key}
                    color="gray.400"
                    _selected={{ color: "white", bg: "gray.700", borderColor: "gray.600", borderBottomColor: "gray.700" }}
                    _hover={{ color: "white" }}
                    whiteSpace="nowrap"
                    fontSize="sm"
                  >
                    {tab.label}
                  </Tab>
                ))}
              </TabList>

              <TabPanels>
                {visibleTabs.map(tab => {
                  const TabComponent = TAB_COMPONENTS[tab.key];
                  return (
                    <TabPanel key={tab.key} px={0} pt={6}>
                      {TabComponent ? <TabComponent /> : <Text>Tab not found</Text>}
                    </TabPanel>
                  );
                })}
              </TabPanels>
            </Tabs>
          </VStack>
        </form>
      </Container>
    </Box>
  );
}

export default function InstitutionConfigPage() {
  return (
    <InstitutionConfigProvider>
      <InstitutionConfigContent />
    </InstitutionConfigProvider>
  );
}
